import { useState, useEffect, useRef, useCallback } from 'react';
import { X, User, AlertCircle } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { CachedImage } from '../CachedImage';
import { VideoThumbnail } from '../VideoThumbnail';
import { LoadingSpinner } from '../LoadingSpinner';

interface Asset {
  id: string;
  public_url: string;
  filename?: string;
  media_type?: 'image' | 'video' | 'audio';
  file_extension?: string;
  created_at: string;
  app_id: string;
  route_name?: string;
}

interface UserAssetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

export const UserAssetsModal = ({ isOpen, onClose, userId, userName }: UserAssetsModalProps) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const observerRef = useRef<HTMLDivElement>(null);

  const ASSETS_PER_PAGE = 10;

  const fetchAssets = async (offset: number = 0, append: boolean = false) => {
    try {
      if (offset === 0) setLoading(true);
      setError(null);

      // Fetch assets for specific user with pagination
      const { data: assetsData, error: assetsError } = await supabase
        .from('assets')
        .select('id, public_url, filename, media_type, file_extension, created_at, app_id')
        .eq('hy_user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + ASSETS_PER_PAGE - 1);

      if (assetsError) {
        throw assetsError;
      }

      if (!assetsData || assetsData.length === 0) {
        setHasMore(false);
        if (offset === 0) setAssets([]);
        return;
      }

      // Get unique app IDs for route name lookup
      const appIds = [...new Set(assetsData.map(asset => asset.app_id).filter(Boolean))];

      // Create app ID to route name mapping
      const appRouteMap: Record<string, string> = {};

      if (appIds.length > 0) {
        const { data: pagesData } = await supabase
          .from('pages')
          .select('app_id, route_path')
          .in('app_id', appIds);

        if (pagesData) {
          pagesData.forEach(page => {
            if (page.app_id && page.route_path) {
              appRouteMap[page.app_id] = page.route_path;
            }
          });
        }
      }

      // Add route names to assets
      const enrichedAssets = assetsData.map(asset => ({
        ...asset,
        route_name: appRouteMap[asset.app_id] || asset.app_id || 'Unknown'
      }));

      if (append) {
        setAssets(prev => [...prev, ...enrichedAssets]);
      } else {
        setAssets(enrichedAssets);
      }

      setCurrentOffset(offset + ASSETS_PER_PAGE);
      setHasMore(assetsData.length === ASSETS_PER_PAGE);

    } catch (err) {
      console.error('Error fetching user assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAssets = useCallback(() => {
    if (hasMore && !loading) {
      fetchAssets(currentOffset, true);
    }
  }, [hasMore, loading, currentOffset, userId]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreAssets();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreAssets, hasMore, loading]);

  // Load initial assets when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      setAssets([]);
      setCurrentOffset(0);
      setHasMore(true);
      setError(null);
      fetchAssets(0, false);
    }
  }, [isOpen, userId]);

  const getAssetMediaType = (asset: Asset): 'image' | 'video' | 'audio' => {
    if (asset.media_type) return asset.media_type;

    const extension = asset.file_extension?.toLowerCase() ||
                      asset.filename?.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
    return 'audio';
  };

  const renderAssetItem = (asset: Asset) => {
    const mediaType = getAssetMediaType(asset);

    return (
      <div key={asset.id} className="break-inside-avoid mb-4">
        <div className="bg-elevated rounded-xl overflow-hidden shadow-lg hover:scale-[1.02] transition-all">
          {/* Asset Preview */}
          <div className="relative bg-surface">
            {mediaType === 'image' ? (
              <CachedImage
                src={asset.public_url}
                alt="User asset"
                className="w-full h-auto object-cover"
                fallback={
                  <div className="w-full h-32 flex items-center justify-center text-th-secondary bg-elevated">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    </svg>
                  </div>
                }
                onClick={() => {
                  // This component doesn't have setPreviewAsset, so we'll handle it differently
                  console.log('Image clicked:', asset.filename);
                }}
              />
            ) : mediaType === 'video' ? (
              <div className="w-full">
                <VideoThumbnail
                  src={asset.public_url}
                  className="w-full"
                  showPlayButton={true}
                 onClick={() => {
                   console.log('Video clicked:', asset.filename);
                 }}
                />
              </div>
            ) : (
              <div
                className="w-full bg-status-warning/20 flex items-center justify-center"
                style={{ aspectRatio: '16/9' }}
              >
                <svg className="w-8 h-8 text-status-warning" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            )}
          </div>

          {/* Route Name */}
          <div className="p-3">
            <p className="text-sm font-medium text-th-primary truncate">
              {asset.route_name}
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base z-modal-content flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-subtle bg-surface">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-th-primary">{userName}'s Assets</h2>
            <p className="text-sm text-th-secondary">
              {assets.length} asset{assets.length !== 1 ? 's' : ''} loaded
              {hasMore && ' • Loading more...'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading && assets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary">Loading assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4 max-w-md">
              <AlertCircle className="w-16 h-16 text-status-error mx-auto" />
              <h3 className="text-xl font-bold text-th-primary">Failed to Load Assets</h3>
              <p className="text-th-secondary">{error}</p>
              <button
                onClick={() => fetchAssets(0, false)}
                className="button-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : assets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="text-6xl mb-4">📁</div>
              <h3 className="text-xl font-bold text-th-primary">No Assets Found</h3>
              <p className="text-th-secondary">This user hasn't generated or uploaded any assets yet.</p>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            {/* Masonry Grid */}
            <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4">
              {assets.map(asset => renderAssetItem(asset))}
            </div>

            {/* Loading More Indicator */}
            {loading && assets.length > 0 && (
              <div className="text-center py-8">
                <LoadingSpinner size="md" />
                <p className="text-th-secondary mt-2">Loading more assets...</p>
              </div>
            )}

            {/* End of Results */}
            {!hasMore && assets.length > 0 && (
              <div className="text-center py-8">
                <p className="text-th-secondary">
                  All {assets.length} asset{assets.length !== 1 ? 's' : ''} loaded
                </p>
              </div>
            )}

            {/* Intersection Observer Target */}
            {hasMore && (
              <div ref={observerRef} className="h-10 flex items-center justify-center">
                <div className="w-2 h-2 bg-brand rounded-full animate-pulse"></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};
